import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import MatrixRain from "../components/MatrixRain";
import { Footer } from "../components/Sections";
import { ASSETS } from "../mock";
import { usePageTracking, track, getVisitorId, getStoredUTM, getCalendlyUtm } from "../analytics";
import { whopTrack } from "../pixels";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CALENDLY_URL =
  "https://calendly.com/d/dtd2-p3j-658/strategy-call-tmu?hide_event_type_details=1&hide_gdpr_banner=1&background_color=000000&text_color=ffffff&primary_color=16e36f";

const CalendlyInline = ({ onScheduled }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    const init = () => {
      if (window.Calendly && containerRef.current) {
        containerRef.current.innerHTML = "";
        const opts = { url: CALENDLY_URL, parentElement: containerRef.current };
        const utm = getCalendlyUtm();
        if (utm) opts.utm = utm;
        window.Calendly.initInlineWidget(opts);
      }
    };
    const id = "calendly-widget-js";
    let script = document.getElementById(id);
    if (window.Calendly) {
      init();
    } else if (script) {
      script.addEventListener("load", init);
    } else {
      script = document.createElement("script");
      script.id = id;
      script.src = "https://assets.calendly.com/assets/external/widget.js";
      script.async = true;
      script.addEventListener("load", init);
      document.body.appendChild(script);
    }
    return () => script && script.removeEventListener("load", init);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (e.data && e.data.event === "calendly.event_scheduled") {
        onScheduled && onScheduled(e.data.payload || {});
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [onScheduled]);

  return (
    <div
      ref={containerRef}
      style={{ minWidth: "320px", height: "700px" }}
      data-testid="booking-calendly-embed"
    />
  );
};

const BookingCalendarPage = () => {
  usePageTracking("booking_calendar");
  const navigate = useNavigate();
  const firedView = useRef(false);
  const firedBooked = useRef(false);

  useEffect(() => {
    if (firedView.current) return;
    firedView.current = true;
    track("booking_page_view", { funnel: "vsl", step: 3 });
    whopTrack("booking_page_view", { funnel: "vsl", step: 3 });
  }, []);

  const handleScheduled = (payload) => {
    if (firedBooked.current) return;
    firedBooked.current = true;
    const txn = payload?.invitee?.uri || payload?.event?.uri || `vsl_${Date.now()}`;
    // Do NOT fire call_booked here. Set a flag + forward to /thankyou, where
    // call_booked fires ONLY after this /calendar-booking booking is confirmed.
    sessionStorage.setItem("tmu_vsl_booked", "1");
    sessionStorage.setItem("tmu_vsl_txn", txn);
    sessionStorage.setItem("tmu_vsl_event_uri", payload?.event?.uri || "");
    // Persist the invitee (name/email fetched server-side from Calendly) and
    // forward this VSL booking to the ipdatime webhook.
    fetch(`${API}/vsl/booking`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        invitee_uri: payload?.invitee?.uri,
        event_uri: payload?.event?.uri,
        txn,
        visitor_id: getVisitorId(),
        utm: getStoredUTM(),
      }),
    }).catch(() => {});
    track("booking_confirmed_pending", {
      funnel: "vsl",
      source: "calendar-booking",
      event_uri: payload?.event?.uri,
      invitee_uri: payload?.invitee?.uri,
    });
    setTimeout(() => navigate("/thankyou"), 1200);
  };

  return (
    <div className="min-h-screen bg-black">
      <header className="relative overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-[360px]">
          <MatrixRain />
          <div className="matrix-fade" />
        </div>

        <div className="relative z-10 mx-auto flex max-w-4xl flex-col items-center px-5 pt-10">
          <img
            src={ASSETS.logoEmblem}
            alt="The Matrix Unlocked"
            className="h-16 w-auto sm:h-20"
          />
          <h1 className="mt-5 text-balance text-center text-2xl font-semibold tracking-tight text-white sm:text-4xl md:text-[44px]">
            Book Your <span className="text-[#16E36F]">Free Call</span> Below
          </h1>
        </div>

        <div className="relative z-10 mx-auto mt-8 w-full max-w-xl px-4 pb-16 sm:px-5">
          <div className="overflow-hidden rounded-2xl border-2 border-[#16E36F] bg-black shadow-[0_20px_80px_rgba(0,0,0,0.6)]">
            <CalendlyInline onScheduled={handleScheduled} />
          </div>
        </div>
      </header>

      <Footer />
    </div>
  );
};

export default BookingCalendarPage;
